import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import Modal from '../Modal';
import { InlineError, apiErrorMessage } from '../sales/shared';
import { DOCUMENT_ACCEPT, checkDocumentFile, formatBytes, titleFromFileName } from './documentFiles';
import { folderPickerOptions } from './folderExplorerModel';
import EntityPicker from '../ui/EntityPicker';
import type { DocumentClassification, DocumentFolder, DocumentType, OteDocument } from '../../types';

export const DOCUMENT_TYPES: DocumentType[] = [
  'general', 'quote', 'proposal', 'contract', 'delivery', 'characterization', 'report', 'checklist', 'other',
];
export const CLASSIFICATIONS: DocumentClassification[] = ['internal', 'client_visible', 'financial_sensitive'];

/**
 * Create a document, optionally together with its first file (multipart).
 * New documents are internal by default; making one visible in the customer
 * portal is a separate, explicit publish step (decision 57).
 */
export default function DocumentUploadModal({
  organizationId,
  folderId,
  folders,
  onClose,
  onCreated,
}: {
  organizationId: string;
  /** Pre-selected folder (e.g. the folder currently open). */
  folderId?: string | null;
  /** When given, the user may pick the folder. */
  folders?: DocumentFolder[];
  onClose: () => void;
  onCreated: (doc: OteDocument) => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<DocumentType>('general');
  const [classification, setClassification] = useState<DocumentClassification>('internal');
  const [description, setDescription] = useState('');
  const [versionNote, setVersionNote] = useState('');
  const [targetFolder, setTargetFolder] = useState<string>(folderId ?? '');
  // DQ-290: a double click must not create two documents (isPending is only set after the next render).
  const createInFlight = useRef(false);

  const mutation = useMutation({
    mutationFn: () => {
      const fields = {
        organization_id: organizationId,
        title: title.trim(),
        folder_id: targetFolder || null,
        document_type: docType,
        description: description.trim() || null,
        classification,
      };
      return file
        ? api.createDocumentWithFile({ ...fields, version_note: versionNote.trim() || null }, file)
        : api.createDocument({ ...fields, status: 'draft' });
    },
    onSuccess: (res) => {
      if (res.data) onCreated(res.data);
    },
  });

  const pickFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      setFileProblem(null);
      return;
    }
    const problem = checkDocumentFile(f);
    if (problem) {
      setFile(null);
      setFileProblem(problem === 'type' ? t('documents.upload.badType') : t('documents.upload.tooLarge'));
      return;
    }
    setFileProblem(null);
    setFile(f);
    if (!title.trim()) setTitle(titleFromFileName(f.name));
  };

  return (
    <Modal open title={t('documents.createDocument')} onClose={onClose} sizeClass="max-w-lg"
      icon={<Upload size={18} className="text-surface-500" />}>
      <div className="space-y-4 max-h-[75vh] overflow-y-auto">
        <div>
          <label className="label" htmlFor="doc-upload-file">{t('documents.upload.file')}</label>
          <input id="doc-upload-file" type="file" accept={DOCUMENT_ACCEPT} className="input"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
          {file ? (
            <p className="text-xs text-surface-600 mt-1 inline-flex items-center gap-1">
              <FileText size={12} /> {file.name} · {formatBytes(file.size)}
            </p>
          ) : (
            <p className="text-xs text-surface-600 mt-1">{t('documents.upload.fileHint')}</p>
          )}
          {fileProblem && <p className="error-text" role="alert">{fileProblem}</p>}
        </div>
        <div>
          <label className="label" htmlFor="doc-upload-title">{t('documents.documentTitle')} <span className="text-danger-700" aria-hidden="true">*</span></label>
          <input id="doc-upload-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="doc-upload-type">{t('documents.type')}</label>
            <select id="doc-upload-type" className="input" value={docType} onChange={(e) => setDocType(e.target.value as DocumentType)}>
              {DOCUMENT_TYPES.map((ty) => <option key={ty} value={ty}>{t(`documents.type_${ty}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="doc-upload-class">{t('documents.classificationLabel')}</label>
            <select id="doc-upload-class" className="input" value={classification}
              onChange={(e) => setClassification(e.target.value as DocumentClassification)}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{t(`documents.classification.${c}`)}</option>)}
            </select>
          </div>
        </div>
        {classification === 'client_visible' && (
          <p className="text-xs text-surface-600">{t('documents.publishLaterHint')}</p>
        )}
        {folders && folders.length > 0 && (
          <div>
            <label className="label" htmlFor="doc-upload-folder">{t('documents.folder')}</label>
            <EntityPicker
              id="doc-upload-folder"
              value={targetFolder}
              onChange={setTargetFolder}
              emptyLabel={t('documents.noFolder')}
              options={folderPickerOptions(folders)}
            />
          </div>
        )}
        <div>
          <label className="label" htmlFor="doc-upload-desc">{t('documents.description')}</label>
          <textarea id="doc-upload-desc" className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {file && (
          <div>
            <label className="label" htmlFor="doc-upload-note">{t('documents.versionNote')}</label>
            <input id="doc-upload-note" className="input" value={versionNote} onChange={(e) => setVersionNote(e.target.value)} />
          </div>
        )}
        {mutation.isError && <InlineError>{apiErrorMessage(mutation.error, t('documents.actionError'))}</InlineError>}
        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={!title.trim() || mutation.isPending}
            aria-busy={mutation.isPending || undefined}
            onClick={() => {
              if (createInFlight.current) return;
              createInFlight.current = true;
              mutation.mutate(undefined, { onSettled: () => { createInFlight.current = false; } });
            }}>
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {mutation.isPending ? (file ? t('common.uploading') : t('common.creating')) : t('common.create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
